import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { Subject } from "rxjs";

import { BaseComponent } from "src/ancestors/base/base.component";
import { UrlCollection } from "./app-routing.module";
import { TranslateService } from "./shared/util/translate.service";
import { BaseLoginInfo } from "./global-service/user.service";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { TenantService } from "./global-service/tenant.service";
import { TenantStore } from "./state/tenant.store";
import { takeUntil } from "rxjs/operators";
import { I18n } from "src/ancestors/i18n/i18n.module";
import { StartupPushNotificationService } from "./global-service/startup-push-notification.service";
import { OfflineDetectionService } from "./global-service/offline-detection.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  protected fb: FormBuilder = inject(FormBuilder);
  protected translate = inject(TranslateService);
  protected envConfig: EnvironmentLoaderService = inject(EnvironmentLoaderService);
  // private tkStorage: TokenStorageService = inject(TokenStorageService);
  private tenantService = inject(TenantService);
  private tenantStore = inject(TenantStore);
  private titleService = inject(Title);
  private i18n = inject(I18n);
  private startupPushService = inject(StartupPushNotificationService);
  private offlineDetectionService = inject(OfflineDetectionService);
  private destroy$ = new Subject<void>();

  // Stato per il modale offline
  protected isOfflineModalOpen = false;

  constructor() {
    super();
    this.logger.setCaller("AppComponent");
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  override async ngOnInit(): Promise<void> {
    // Load tenant information first
    await this.loadTenantInfo();

    const loginResponse: BaseLoginInfo = this.tStorage.retriveToken(this.tokenKey);

    // Load user information and extract locale
    if (loginResponse?.jwtPayload) {
      // Extract and store locale from JWT payload if present
      await this.extractAndStoreLocale(loginResponse);
    }

    /**
     * 
     * Se esiste un token ed è ancora valido schedula il rinnovo prima della scadenza
     * Se esiste un token ma è scaduto, lo rimuove dallo storage e reindirizza al login
    */
    if (!this.appService.isEmpty(loginResponse)) {
      if (this.tkManager.checkTokenValidity(loginResponse)) {
        this.tkManager.refreshTokenScheduler(loginResponse);
      } else {
        // Token exists but is expired - clean it up and redirect to login
        this.logger.debug("Expired token detected at startup, removing from storage and redirecting to login");
        this.tStorage.deleteToken(this.tokenKey);
        await this.router.navigate([this.env.loginUrl]);
        return; // Exit early to prevent further initialization
      }
    }

    if (!sessionStorage.getItem("originalRequestedUrl") && this.env.loginType === "saml-sso") {
      await this.router.navigate([UrlCollection.redirectTo]);
    }

    sessionStorage.removeItem("originalRequestedUrl");

    // Initialize push notifications after everything else is set up
    // This avoids circular dependency issues during app initialization
    this.initializePushNotifications();

    // Initialize offline detection
    this.initializeOfflineDetection();
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  override async ngOnDestroy(): Promise<void> {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Extract locale from JWT payload and store it in localStorage
   * If locale changes, reload translations
   */
  private async extractAndStoreLocale(loginResponse: BaseLoginInfo): Promise<void> {
    try {
      const userLocale = loginResponse.jwtPayload.user?.locale;
      const currentStoredLocale = localStorage.getItem("locale");
      if (userLocale && !currentStoredLocale) {

        // Store the locale in localStorage
        localStorage.setItem("locale", userLocale);

        // If locale has changed, reload translations
        if (currentStoredLocale !== userLocale) {
          this.logger.debug(`Locale changed from ${currentStoredLocale} to ${userLocale}, reloading translations`);

          // Update the i18n service locale
          this.i18n.locale = userLocale;

          // Reload translations
          await this.i18n.setLocale();

          // Reload the translation service
          await this.translate.reloadTranslations();
        }
      }
    } catch (error) {
      // do nothing
    }
  }

  /**
   * Load tenant information and set page title
   */
  private async loadTenantInfo(): Promise<void> {
    try {
      await this.tenantService.loadTenantInfo();

      // Subscribe to tenant title changes to update page title
      this.tenantStore.tenantTitle$
        .pipe(takeUntil(this.destroy$))
        .subscribe(title => {
          if (title) {
            this.titleService.setTitle(title);
          }
        });
    } catch (error) {
      // redirect to error page
      await this.router.navigate([UrlCollection.genericErrorPage]);
    }
  }

  /**
   * Initialize push notifications after app is fully loaded
   * This method ensures proper initialization timing and avoids circular dependencies
   */
  private initializePushNotifications(): void {
    try {
      // Use setTimeout to ensure this runs after the current execution stack
      setTimeout(() => {
        this.startupPushService.initializeIfAuthenticated().catch(error => {
          this.logger.error(`Failed to initialize push notifications: ${error instanceof Error ? error.message : String(error)}`);
        });
      }, 0);
    } catch (error) {
      this.logger.error(`Error setting up push notification initialization: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  
  private isUserAuthenticated(): boolean {
    const token = this.tStorage.retriveToken(this.tokenKey);
    return !!token?.jwtPayload;
  }

  /**
   * Inizializza il rilevamento dello stato offline/online
   * Mostra un modale non rimovibile quando l'utente va offline
   */
  private initializeOfflineDetection(): void {
    try {
      // Sottoscrivi ai cambiamenti dello stato offline
      this.offlineDetectionService.isOffline$
        .pipe(takeUntil(this.destroy$))
        .subscribe(isOffline => {
          this.logger.debug(`App offline status changed: ${isOffline ? "Offline" : "Online"}`);
          if (!this.isUserAuthenticated()) {
            this.isOfflineModalOpen = false;
            return;
          }
          this.isOfflineModalOpen = isOffline;
        });

      this.logger.debug("Offline detection initialized successfully");
    } catch (error) {
      this.logger.error(`Error initializing offline detection: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Testa manualmente la connessione di rete
   * Metodo esposto per essere chiamato dal template
   */
  protected async testConnection(): Promise<void> {
    this.logger.debug("Manual connection test initiated");
    try {
      await this.offlineDetectionService.forceUpdateConnectionStatus();
    } catch (error) {
      this.logger.error(`Manual connection test failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /** Questa funzione cambia la lingua a runtime, e fa il reload della pagina */
  private changeLang(locale: string) {

    /** Salva la lingua nel localStorage */
    localStorage.setItem("locale", locale);

    /** Fa il reload della pagina, per caricare il file json corretto */
    location.reload();
  }
}
